import { onMount, Show } from 'solid-js'
import { m } from '~/paraglide/messages'
import { usePurchaseStats } from '~/resources/purchase-stats'
import { Milestone } from './checkpoint'
import { getCheckpoints, getMaxEnergy } from './config'
import './flag-animation.css'

export function Progress() {
  const purchaseStatsQuery = usePurchaseStats()
  let scrollContainerRef: HTMLDivElement | undefined

  onMount(() => {
    setTimeout(() => {
      if (!scrollContainerRef) return
      const progressPos = (690 * progressPercent()) / 100
      const containerWidth = scrollContainerRef.clientWidth
      const scrollTarget = Math.max(0, progressPos - containerWidth / 2)
      scrollContainerRef.scrollTo({
        left: scrollTarget,
        behavior: 'smooth',
      })
    }, 800)
  })

  const totalEnergy = () => {
    return (purchaseStatsQuery.data?.stats?.[0]?.total_price ?? 0) * 2.5
  }

  const hasReachedLastCheckpoint = () => totalEnergy() >= getMaxEnergy()

  const progressPercent = () => {
    const energy = totalEnergy()
    if (energy >= getMaxEnergy()) return 100
    if (energy <= 0) return 0

    // 4 equal segments, each 25% of the bar
    for (let i = 4; i >= 1; i--) {
      const cpEnergy = getCheckpoints()[i].energyAmount
      const prevEnergy = i > 1 ? getCheckpoints()[i - 1].energyAmount : 0
      if (energy >= prevEnergy) {
        if (energy >= cpEnergy) continue
        const segmentProgress = (energy - prevEnergy) / (cpEnergy - prevEnergy)
        return (i - 1) * 25 + segmentProgress * 25
      }
    }
    return 0
  }

  const formatEnergy = (energy: number) => {
    const k = energy / 1000
    if (k >= 100) return `${Math.round(k)}K`
    if (k >= 10) return `${k.toFixed(k % 1 === 0 ? 0 : 1)}K`
    return `${k.toFixed(k % 1 === 0 ? 0 : 2)}K`
  }

  // The bar's vertical center aligns with the milestone dots.
  // Milestone layout: label (20px) + gap (4px) + dot center (13px) = 37px from top.
  // Milestone row starts at padding-top 5px. Bar center = 5 + 37 = 42px.
  // Bar height = 12px, so bar top = 42 - 6 = 36px.
  const barTop = 40

  return (
    <div class="isolate mt-[-10px]">
      <div
        data-progress-scroll
        class="no-scrollbar relative overflow-x-auto pl-10 pt-[10px]"
        ref={scrollContainerRef}
      >
        <div class="relative w-[900px]">
          {/* Progress bar - positioned to align with milestone dots */}
          <div
            data-energy-target
            class="absolute left-0 h-[12px] w-[690px] rounded-[30px] bg-[#037067] p-1"
            style={{ top: `${barTop}px` }}
          >
            {/* Filled portion - thin gradient line within the bar */}
            <div class="relative h-full rounded-[30px]">
              <div
                class="absolute inset-y-0 left-0 rounded-[30px] bg-gradient-to-r from-[#8DFFF7] to-white"
                style={{ width: `${progressPercent().toFixed(1)}%` }}
              />
            </div>

            {/* Glowing progress indicator */}
            <Show when={totalEnergy() > 0}>
              <div
                class="absolute top-1/2 z-10 size-[26px] -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.7)_0%,rgba(221,255,247,0)_100%)]"
                style={{ left: `${progressPercent().toFixed(1)}%` }}
              >
                <div class="absolute left-1/2 top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
              </div>
            </Show>
          </div>

          {/* "0" label */}
          <span class="absolute left-[2px] top-[5px] text-sm font-bold leading-[20px] text-white">
            {'0'}
          </span>

          {/* Milestones row */}
          <div class="relative flex gap-[14px]">
            <div class="relative flex gap-[56px] pl-[112px] pt-[5px]">
              <Milestone checkpoint={1} currentEnergy={totalEnergy()} />
              <Milestone checkpoint={2} currentEnergy={totalEnergy()} />
              <Milestone checkpoint={3} currentEnergy={totalEnergy()} />
              <Milestone checkpoint={4} currentEnergy={totalEnergy()} />
            </div>

            <Show when={hasReachedLastCheckpoint()}>
              {/* Full energy section */}
              <div class="absolute right-[60px] flex w-[117px] items-center">
                <p class="relative whitespace-pre-line text-sm font-semibold text-[#FDFFB4]">
                  <span>{m.goal_reached()}</span>
                </p>

                <div class="flag-wrap relative left-[-22px] top-[-12px] rotate-12 scale-50">
                  <div class="flag-sparks">
                    <div class="flag-spark flag-spark-left" />
                    <div class="flag-spark flag-spark-mid" />
                    <div class="flag-spark flag-spark-right" />
                  </div>
                  <div class="flag-pole" />
                  <div class="flag-triangle" />
                </div>
              </div>
            </Show>
          </div>

          {/* Floating progress bubble */}
          <Show when={totalEnergy() > 0}>
            <div
              class="absolute -top-1 -translate-x-1/2"
              style={{
                left: `${(690 * progressPercent()) / 100 + (hasReachedLastCheckpoint() ? 32 : 15)}px`,
              }}
            >
              <div class="flex h-[30px] items-center justify-center rounded-[50px] bg-white px-3">
                <span
                  class="font-rubik text-sm font-bold text-[#1b472b]"
                  style={
                    hasReachedLastCheckpoint()
                      ? {
                          background:
                            'linear-gradient(180deg, #884F00 0%, #221400 100%)',
                          'background-clip': 'text',
                          '-webkit-background-clip': 'text',
                          '-webkit-text-fill-color': 'transparent',
                        }
                      : undefined
                  }
                >
                  {hasReachedLastCheckpoint()
                    ? `${getMaxEnergy() / 1000}K+`
                    : formatEnergy(totalEnergy())}
                </span>
              </div>
              <div
                class="ml-3 size-0"
                style={{
                  'border-left': '4px solid transparent',
                  'border-right': '4px solid transparent',
                  'border-top': '4px solid white',
                }}
              />
            </div>
          </Show>
        </div>
      </div>
    </div>
  )
}
